import React, { FC } from "react";
// libs
import { useForm, Controller } from "react-hook-form";
import axios from "src/libs/axios";
import Modal from "react-modal";
// components
import PhoneInput from "src/components/ui/phoneInput/PhoneInput";
import Input from "src/components/ui/input";
import Button from "src/components/ui/button";
// hooks
import { useWindowSize } from "src/hooks";
// const
import { EVariants } from "src/constants/variants";
import { ENDPOINTS } from "src/constants/endpoints";
// types
import { TClinicDetails } from "src/types";
// theme
import { FormContainer, ButtonContainer, ButtonBox, ButtonBoxCancel } from "./Form.styles";
import { amplitudeEvent, formatEventName } from "src/libs/amplitude";
import { Body, Caption, Headline, LargeTitle } from "src/theme/typography";
import { FlexColumn } from "src/components/ui/common";
import { colors } from "src/theme";

interface IForm {
  clinic: TClinicDetails;
  isOpen: boolean;
  handleCloseModal: () => void;
  GoNext: () => void;
  setStep: (data: number) => void;
}

const Form: FC<IForm> = ({ clinic, isOpen, handleCloseModal, GoNext, setStep }) => {
  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm();
  const { width } = useWindowSize();

  const onSubmit = async (data: any) => {
    amplitudeEvent(formatEventName("clinic_details", "claim_clinic", "submit"));

    const dataForm = {
      name: data.name,
      email: data.email,
      phone: data.phoneCode + data.phoneNumber,
      clinic,
    };

    axios
      .post(ENDPOINTS.POST.claimYourClinic, dataForm)
      .then((resp) => {
        if (resp.status >= 200 && resp.status < 300) {
          GoNext();
        }
      })
      .catch((error) => {
        console.log(error);
        setStep(3);
      });
  };

  const overlay = {
    zIndex: 3000,
    backgroundColor: "rgba(46, 46, 46, 0.75)",
  };

  let content: { [key: string]: string } = {
    padding: "24px",
    border: "none",
    width: "100vw",
    borderRadius: "0",
    top: "0px",
    left: "0px",
    bottom: "0px",
    right: "0px",
  };

  if (width >= 768) {
    content = {
      ...content,
      padding: "24px",
      width: "480px",
      height: "fit-content",
      borderRadius: "8px",
      top: "calc(50% - 300px)",
      left: "calc(50% - 240px)",
    };
  }

  return (
    <Modal isOpen={isOpen} style={{ overlay, content }} onRequestClose={handleCloseModal}>
      <LargeTitle>Just a few details first&hellip;</LargeTitle>
      <Body style={{ marginBottom: 24 }}>Then we can start your setup</Body>

      <FormContainer onSubmit={handleSubmit(onSubmit)}>
        <FlexColumn gap="16px">
          <FlexColumn gap="8px">
            <Headline>Name</Headline>
            <Input
              register={{ ...register("name", { required: "Name is required" }) }}
              errors={errors.name}
              style={{ height: "30px" }}
            />
          </FlexColumn>

          <FlexColumn gap="8px">
            <Headline>Email</Headline>
            <Input
              register={{
                ...register("email", { required: "Email is required", pattern: /\S+@\S+\.\S+/ }),
              }}
              errors={errors.email}
              style={{ height: "30px" }}
              placeholder="jenny@example.com"
            />
          </FlexColumn>

          <FlexColumn gap="8px">
            <Headline>Phone number</Headline>
            <Controller
              name="phoneNumber"
              control={control}
              rules={{ required: true, minLength: 8, maxLength: 15 }}
              render={({ field, fieldState: { error } }) => {
                return (
                  <>
                    <PhoneInput
                      styles={{ width: "100%" }}
                      errors={errors.phoneNumber}
                      {...field}
                      setCode={(value) => {
                        setValue("phoneCode", value);
                      }}
                    />
                    {error && (
                      <Caption textColor={colors.redDark}>Phone number is required</Caption>
                    )}
                  </>
                );
              }}
            />
          </FlexColumn>

          <ButtonContainer>
            <ButtonBoxCancel>
              <Button
                variant={EVariants.SECONDARY}
                style={{ width: "100%", boxShadow: "none" }}
                onClick={handleCloseModal}
              >
                Cancel
              </Button>
            </ButtonBoxCancel>
            <ButtonBox>
              <Button
                variant={EVariants.PRIMARY}
                type="submit"
                style={{ width: "100%", boxShadow: "none" }}
              >
                Submit
              </Button>
            </ButtonBox>
          </ButtonContainer>
        </FlexColumn>
      </FormContainer>
    </Modal>
  );
};

export default Form;
